import {useEffect, useRef, useState} from 'react'
import {Button, Checkbox, Modal} from 'antd'
import {LoadingOutlined} from '@ant-design/icons'
import {css} from '@emotion/react'
import {Alert, useOpenModal} from '@/components/Modal'
import {useAiContext} from '@/biz/ai'

const cssPlanItems = css({
    padding: '4px 0',
    borderTop: '1px solid #e8e8e8',
    borderBottom: '1px solid #e8e8e8',
    margin: '4px 0',
})

const cssPlan = css({
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    alignItems: 'center',
    gap: 12,
})

const cssAction = css({
    cursor: 'pointer',

    '&::before': {
        content: '""',
        fontWeight: 'bold',
        marginRight: 4,
    },
})

const cssActionAdd = css({
    color: '#2e7d32',

    '&::before': {
        content: '"A"',
    },
})

const cssActionDelete = css({
    color: '#c83c3c',

    '&::before': {
        content: '"D"',
    },
})

const cssActionModify = css({
    color: '#b48c00',

    '&::before': {
        content: '"M"',
    },
})

const Plan = ({plan, onApply, ...props}) => {
    const {map} = useAiContext()
    const {action, isCommitted, relatedNode, targetNode} = plan

    const [node, _cssAction] = {
        delete: [targetNode, cssActionDelete],
        replace: [targetNode, cssActionModify],
        append: [relatedNode, cssActionAdd],
        prepend: [relatedNode, cssActionAdd],
        insertBefore: [relatedNode, cssActionAdd],
        insertAfter: [relatedNode, cssActionAdd],
    }[action]

    const _n = map.nodeProxy(node)
    const text = _n.getTextRaw()
    const onChange = e => onApply(e.target.checked)
    const onClick = () => map.selectNodes([node])

    return (
        <div
            css={cssPlan}
            {...props}
        >
            <Checkbox
                checked={isCommitted}
                onChange={onChange}
            />

            <span
                css={[cssAction, _cssAction]}
                onClick={onClick}
            >
                {text}
            </span>
        </div>
    )
}

const cssPlanButton = css({
    fontSize: 12,
})

const PlanButton = props => {
    return (
        <Button
            css={cssPlanButton}
            size="small"
            type="link"
            {...props}
        />
    )
}

const cssPlans = css({
    width: '100%',
})

const Plans = ({plans, onApply, onCancel, onConfirm, ...props}) => {
    const items = plans.map((p, i) => (
        <Plan
            key={i}
            plan={p}
            onApply={isCommitted => onApply(isCommitted, p)}
        />
    ))

    const committedCount = plans.filter(e => e.isCommitted).length
    const checked = committedCount === plans.length
    const indeterminate = ! checked && 0 <committedCount

    const onChange = () => {
        const isCommitted = ! checked

        for (const p of plans) {
            onApply(isCommitted, p)
        }
    }

    return (
        <div
            css={cssPlans}
            {...props}
        >
            <div css={cssPlan}>
                <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={onChange}
                />

                {committedCount} / {plans.length}
            </div>

            <div css={cssPlanItems}>
                {items}
            </div>

            <div>
                <PlanButton onClick={onConfirm}>确定</PlanButton>
                <PlanButton onClick={onCancel}>取消</PlanButton>
            </div>
        </div>
    )
}

const ButtonStartConfirm = ({onClick, ...props}) => {
    const openModal = useOpenModal()
    const ai = useAiContext()

    const _onClick = () => {
        if (ai.isDisabled) {
            openModal(<Alert type="error">已经有计划在确认中</Alert>)
        }
        else {
            onClick()
        }
    }

    return (
        <a
            onClick={_onClick}
            {...props}
        >
            确认计划
        </a>
    )
}

const useSyncMap = (plans, isDone) => {
    const ai = useAiContext()
    const refCommittedPlanIds = useRef(new Set)
    const refLastPlans = useRef(null)

    useEffect(
        () => {
            const commitPlan = ({action, relatedNode, targetNode}) => ({
                delete: () => {
                    targetNode.mode = {
                        code: 'preview',
                        type: 'delete',
                    }
                },

                replace: () => {
                    targetNode.mode = {
                        code: 'preview',
                        type: 'modify',
                        data: targetNode.data,
                    }

                    targetNode.data = relatedNode.data
                },

                append: () => {
                    relatedNode.mode = {
                        code: 'preview',
                        type: 'add',
                    }

                    ai.map.undeleteTree(relatedNode)
                    ai.map.appendChild(targetNode, relatedNode)
                },

                prepend: () => {
                    relatedNode.mode = {
                        code: 'preview',
                        type: 'add',
                    }

                    ai.map.undeleteTree(relatedNode)
                    ai.map.prependChild(targetNode, relatedNode)
                },

                insertBefore: () => {
                    relatedNode.mode = {
                        code: 'preview',
                        type: 'add',
                    }

                    ai.map.undeleteTree(relatedNode)
                    ai.map.insertSiblingBefore(targetNode, relatedNode)
                },

                insertAfter: () => {
                    relatedNode.mode = {
                        code: 'preview',
                        type: 'add',
                    }

                    ai.map.undeleteTree(relatedNode)
                    ai.map.insertSiblingAfter(targetNode, relatedNode)
                },
            })[action]()

            const finishPlan = ({action, relatedNode, targetNode}) => ({
                delete: () => {
                    ai.map.deleteTree(targetNode)
                },

                replace: () => {
                    targetNode.mode = void 0
                },

                append: () => {
                    relatedNode.mode = void 0
                },

                prepend: () => {
                    relatedNode.mode = void 0
                },

                insertBefore: () => {
                    relatedNode.mode = void 0
                },

                insertAfter: () => {
                    relatedNode.mode = void 0
                },
            })[action]()

            const revertPlan = ({action, relatedNode, targetNode}) => ({
                delete: () => {
                    targetNode.mode = void 0
                },

                replace: () => {
                    targetNode.data = targetNode.mode.data
                    targetNode.mode = void 0
                },

                append: () => {
                    ai.map.deleteTree(relatedNode)
                },

                prepend: () => {
                    ai.map.deleteTree(relatedNode)
                },

                insertBefore: () => {
                    ai.map.deleteTree(relatedNode)
                },

                insertAfter: () => {
                    ai.map.deleteTree(relatedNode)
                },
            })[action]()

            const cpids = refCommittedPlanIds.current

            // 确定
            if (isDone) {
                if (plans) {
                    ai.map.execute(() => {
                        for (const p of plans) {
                            if (cpids.has(p.id)) {
                                finishPlan(p)
                            }
                        }
                    })

                    refCommittedPlanIds.current = new Set
                    refLastPlans.current = null
                }
            }
            // 拣选
            else if (plans) {
                ai.map.execute(() => {
                    for (const p of plans) {
                        if (cpids.has(p.id)) {
                            revertPlan(p)
                            cpids.delete(p.id)
                        }
                    }

                    for (const p of plans) {
                        if (p.isCommitted) {
                            commitPlan(p)
                            cpids.add(p.id)
                        }
                    }
                })

                refLastPlans.current = plans
            }
            // 取消
            else if (refLastPlans.current) {
                ai.map.execute(() => {
                    for (const p of refLastPlans.current) {
                        if (cpids.has(p.id)) {
                            revertPlan(p)
                        }
                    }
                })

                refCommittedPlanIds.current = new Set
                refLastPlans.current = null
            }
        },

        [isDone, plans]
    )
}

export const ResponseBodyImportConfirmJson = ({message, ...props}) => {
    const ai = useAiContext()
    const [plans, setPlans] = useState(null)
    const [isDone, setIsDone] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    useSyncMap(plans, isDone)
    const content = JSON.parse(message.content)

    if (content.done || isDone) {
        return <div {...props}>修改计划已确认并执行</div>
    }

    if (isLoading) {
        return (
            <div {...props}>
                <LoadingOutlined /> 标记计划为已处理
            </div>
        )
    }

    if (0 === content.changeList.length) {
        return null
    }

    const startTask = () => {
        ai.map.startTask()
        ai.map.permission.delete('write')
        ai.setIsDisabled(true)
    }

    const finishTask = () => {
        ai.map.permission.add('write')
        ai.map.finishTask()
        ai.setIsDisabled(false)
    }

    if (! plans) {
        const startConfirm = () => {
            const plans = content.changeList
                .map(({action, nodeJsonStr, tNodeId}, id) => {
                    const targetNode = ai.map.getNode(tNodeId)

                    // 有可能已被用户删除
                    if (! targetNode) {
                        return null
                    }

                    const relatedNode = nodeJsonStr &&
                        ai.map.importTree(JSON.parse(nodeJsonStr))

                    return {
                        action,
                        id,
                        isCommitted: true,
                        relatedNode,
                        targetNode,
                    }
                })
                .filter(a => a)

            if (0 < plans.length) {
                startTask()
            }

            setPlans(plans)
        }

        return (
            <div {...props}>
                AI助手已完成修改计划，{content.userMessage}<ButtonStartConfirm onClick={startConfirm} />。
            </div>
        )
    }

    if (0 === plans.length) {
        return (
            <div {...props}>
                修改计划的目标节点已不存在，没有可应用的计划
            </div>
        )
    }

    const onApply = (isCommitted, plan) => {
        setPlans(plans => {
            const i = plans.indexOf(plan)
            return plans.with(i, {...plan, isCommitted})
        })
    }

    const onCancel = () => {
        setPlans(null)
        finishTask()
    }

    const onConfirm = async () => {
        try {
            setIsLoading(true)

            await ai.chat({
                feedbackResult: true,
                userMessage: '确认应用',

                data: {
                    updateMessage: {
                        ...message.raw,
                        messageContent: JSON.stringify({...content, done: true})
                    },
                },
            })

            setIsDone(true)
        }
        catch (err) {
            Modal.error({content: err.message})
        }
        finally {
            setIsLoading(false)
        }

        setPlans(null)
        finishTask()
    }

    return (
        <Plans
            plans={plans}
            onApply={onApply}
            onCancel={onCancel}
            onConfirm={onConfirm}
            {...props}
        />
    )
}
